'use client';

import {useCallback, useEffect, useState} from 'react';
import {FRONTEND_URL} from 'config';
import {useAuth} from 'iq-auth';
import {getUserStreak} from 'iq-auth/api';
import {encodeOAuthState} from './oauth';
import {signupCtaAction, type SignupCtaSurface} from 'iq-auth/signup_cta';
import {trackPostSignupOnboardingRedirected} from 'iq-auth/tracking';
import {isbot} from 'isbot';

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
const LINKEDIN_CLIENT_ID = process.env.NEXT_PUBLIC_LINKEDIN_CLIENT_ID;

export function useStreak(): number {
  const {user} = useAuth();
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    if (!user.loggedIn) return;
    getUserStreak().then((data) => {
      if (data !== null) setStreak(data.streak);
    });
  }, [user.loggedIn]);

  return streak;
}

type OAuthFlow = 'signup' | 'login';

type OAuthUrls = {
  googleUrl: string;
  linkedInUrl: string;
};

export function useOAuthUrls(flow: OAuthFlow, next?: string): OAuthUrls {
  const state = encodeOAuthState(next ? {flow, next} : {flow});

  const googleUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${FRONTEND_URL}/goog&response_type=token&include_granted_scopes=true&scope=email%20profile&state=${state}`;
  const linkedInUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKEDIN_CLIENT_ID}&redirect_uri=${FRONTEND_URL}/lnkd&state=${state}&scope=r_liteprofile%20r_emailaddress`;

  return {googleUrl, linkedInUrl};
}

/**
 * Click handler for a gate's signup button. Call it with the surface's "open the inline
 * AuthForm" action: bots run it unchanged; everyone else goes through the full /signup flow
 * and ends on pricing (shipped to 100% after post_signup_onboarding_v1 won).
 */
export function useSignupCta(surface: SignupCtaSurface): (openInline: () => void) => void {
  return useCallback(
    (openInline: () => void) => {
      const action = signupCtaAction({
        isBot: isbot(window.navigator.userAgent),
        currentPath: `${window.location.pathname}${window.location.search}`,
        frontendUrl: FRONTEND_URL ?? '',
      });
      if (action.kind === 'inline') {
        openInline();
        return;
      }
      void trackPostSignupOnboardingRedirected(surface);
      window.location.href = action.href;
    },
    [surface],
  );
}
